import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, Box, Tab, Tabs, TextField } from "@mui/material";
import { setConfigSpectrometryAlfaTabs } from "../../utils/constants";
import {
  MAutocomplete,
  MTextField,
  MDatePicker,
  TabPanel,
  ResponsiveFormContainer,
} from "@app/components";
import {
  useDetector,
  useEquipment,
  useIsotopos,
  useUser,
} from "@services/servicesGenericsTables";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useFormContext } from "react-hook-form";
import { handleInputChange } from "@app/components/MTextField";
import {
  CalibrationPatternSourceType,
  RowDataCalibrationSpectrometry,
  RowDataCalibrationSpectrometryDetector,
} from "@app/types/report.types";
import { usecalibrationPatternSource } from "../../utils/usecalibrationPatternSource";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

interface AnalysisFormProps {
  mode: string;
}

const addRow: CalibrationPatternSourceType = {
  id: `new-${Date.now()}5`,
  isotope: null,
  activity_bq: "",
  activity_dpm: 0,
  counting_time: "",
  accounts: "",
  efficiency: 0,
};

const initialRowsSpectrometry: RowDataCalibrationSpectrometry[] = [
  {
    id: `new-${Date.now()}1`,
    isotope: 122,
    activity_bq: 0,
    activity_dpm: 0,
    counting_time: 0,
    accounts: 0,
    efficiency: 0,
  },
  {
    id: `new-${Date.now()}2`,
    isotope: 123,
    activity_bq: 0,
    activity_dpm: 0,
    counting_time: 0,
    accounts: 0,
    efficiency: 0,
  },
  {
    id: `new-${Date.now()}3`,
    isotope: 124,
    activity_bq: 0,
    activity_dpm: 0,
    counting_time: 0,
    accounts: 0,
    efficiency: 0,
  },
  {
    id: `new-${Date.now()}4`,
    isotope: null,
    activity_bq: "",
    activity_dpm: 0,
    counting_time: 0,
    accounts: 0,
    efficiency: 0,
  },
];

const AnalysisSpectrometryAlfa = ({ mode }: AnalysisFormProps) => {
  const { isotoposList } = useIsotopos();
  const { idCalibration } = useParams();
  const { equipmentList, loading: loadingEquipment, fetchAll } = useEquipment();
  const { detectorsList } = useDetector();
  const { fetchPreloadCalibrationPatternSource, loading } =
    usecalibrationPatternSource();
  const [tabValue, setTabValue] = useState<number>(0);
  const { userList } = useUser();
  const isView = mode === MODES_OPTIONS.VIEW;
  const isEdit = mode === MODES_OPTIONS.EDIT;
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const { watch, setValue } = useFormContext();

  const watchEquipment = watch("equipment");

  const filterDetectors = useMemo(() => {
    const idsEquipment = watchEquipment?.id;
    return detectorsList.filter(
      (detector) => detector.equipment.id === idsEquipment || false
    );
  }, [watchEquipment, detectorsList]);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if ((isEdit || isView) && idCalibration) {
        try {
          const preload = await fetchPreloadCalibrationPatternSource(
            idCalibration
          );

          setValue(
            "CalibrationPatternSource",
            isEdit ? [...preload, addRow] : [...preload]
          );
        } catch (error) {
          console.error("Error fetching calibration data:", error);
        }
      } else {
        setValue("CalibrationPatternSource", initialRowsSpectrometry);
      }
    };

    fetchData();
  }, [isEdit, isView, idCalibration]);

  const rows = watch("CalibrationPatternSource");

  const handleRowUpdate = (
    id: number | string,
    field: keyof RowDataCalibrationSpectrometry,
    value: any
  ) => {
    const updatedRows = rows.map((row) => {
      let updatedRow = { ...row };

      if (row.id === id) {
        updatedRow = {
          ...updatedRow,
          [field]: value,
          activity_dpm:
            field === "activity_bq"
              ? (Number(value) || 0) * 60
              : updatedRow.activity_dpm,
        };

        if (
          updatedRow.accounts > 0 &&
          updatedRow.counting_time > 0 &&
          updatedRow.activity_dpm > 0
        ) {
          updatedRow.efficiency =
            ((updatedRow.accounts / updatedRow.counting_time) * 60) /
            updatedRow.activity_dpm;
        } else {
          updatedRow.efficiency = 0;
        }
      }

      return updatedRow;
    });

    const lastRow = updatedRows[updatedRows.length - 1];
    if (lastRow.isotope && updatedRows.length === rows.length) {
      updatedRows.push({
        id: `new-${Date.now()}`,
        isotope: "",
        activity_bq: "",
        activity_dpm: 0,
        counting_time: 0,
        accounts: 0,
        efficiency: 0,
      });
    }

    const validRows = updatedRows.filter((row) => row.isotope); // Exclude blank isotopes rows

    const totalEfficiency = validRows.reduce(
      (acc, row) => acc + parseFloat(row.efficiency),
      0
    );

    const meanEfficiency =
      validRows.length > 0 ? totalEfficiency / validRows.length : 0;

    setValue("mean_efficiency", meanEfficiency);

    setValue("CalibrationPatternSource", updatedRows);
  };

  const columns: GridColDef[] = [
    {
      field: "isotope",
      headerName: "Isótopo",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            marginY: 2,
            width: "100%",
            height: "70%",
            display: "flex",
          }}
        >
          <Autocomplete
            options={isotoposList}
            fullWidth
            getOptionLabel={(option) => option.codigo || ""}
            value={isotoposList.find((iso) => iso.id === params.value) || null}
            onChange={(_, newValue) =>
              handleRowUpdate(
                params.id as number,
                "isotope",
                newValue?.id || ""
              )
            }
            disablePortal
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                variant="standard"
                InputLabelProps={{ shrink: false }}
              />
            )}
            disabled={isView}
          />
        </Box>
      ),
    },
    {
      field: "activity_bq",
      headerName: "Actividad (Bq)",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
          }}
        >
          <TextField
            type="text"
            value={params.value || ""}
            onChange={(e) => {
              const formattedValue = handleInputChange(e.target.value);
              handleRowUpdate(
                params.id as number,
                "activity_bq",
                formattedValue
              );
            }}
            fullWidth
            sx={{
              "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                boxSizing: "inherit",
              },
            }}
            disabled={isView}
          />
        </Box>
      ),
    },
    {
      field: "activity_dpm",
      headerName: "Actividad (dpm)",
      flex: 1,
      valueGetter: (params) =>
        typeof params.row.activity_dpm === "number"
          ? params.row.activity_dpm.toFixed(3)
          : params.row.activity_dpm,
    },
  ];

  const columnsDetector: GridColDef[] = [
    {
      field: "date_patron",
      headerName: "Fecha Patrón",
      flex: 1,
      renderCell: (params) => (
        <>
          {params.row.isotope
            ? `${dayjs(watch("date_calibration_certificate")).format(
                "DD/MM/YYYY"
              )}`
            : null}
        </>
      ),
    },
    {
      field: "counting_time",
      headerName: "Tiempo contaje (s)",
      flex: 1,
      renderCell: (params) => (
        <>
          {params.row.isotope ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
              }}
            >
              <TextField
                type="text"
                value={params.value || ""}
                onChange={(e) => {
                  const formattedValue = handleInputChange(e.target.value);
                  handleRowUpdate(
                    params.id as number,
                    "counting_time",
                    formattedValue
                  );
                }}
                fullWidth
                sx={{
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                    boxSizing: "inherit",
                  },
                }}
                disabled={isView}
              />
            </Box>
          ) : null}
        </>
      ),
    },
    {
      field: "isotope",
      headerName: "Isótopo",
      flex: 1,
      renderCell: (params) => {
        return `${
          params.value
            ? isotoposList.find((iso) => iso.id === params.row.isotope)?.codigo
            : ""
        }`;
      },
    },
    {
      field: "accounts",
      headerName: "Cuentas",
      flex: 1,
      renderCell: (params) => (
        <>
          {params.row.isotope ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
              }}
            >
              <TextField
                type="text"
                value={params.value || ""}
                onChange={(e) => {
                  const formattedValue = handleInputChange(e.target.value);
                  handleRowUpdate(
                    params.id as number,
                    "accounts",
                    formattedValue
                  );
                }}
                fullWidth
                sx={{
                  "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                    boxSizing: "inherit",
                  },
                }}
                disabled={isView}
              />
            </Box>
          ) : null}
        </>
      ),
    },
    {
      field: "activity_dpm",
      headerName: "Actividad (dpm)",
      flex: 1,
      valueGetter: (params) =>
        !params.row.isotope
          ? ""
          : typeof params.row.activity_dpm === "number"
          ? params.row.activity_dpm.toFixed(3)
          : params.row.activity_dpm,
    },
    {
      field: "efficiency",
      headerName: "Eficiencia",
      flex: 1,
      valueGetter: (params) =>
        !params.row.isotope
          ? ""
          : typeof params.row.efficiency === "number"
          ? params.row.efficiency.toFixed(3)
          : params.row.efficiency,
    },
  ];

  return (
    <>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        aria-label="Fields uncertainty tabs"
        sx={{ marginBottom: 2 }}
      >
        {setConfigSpectrometryAlfaTabs.map((set, index) => (
          <Tab key={index} label={set.label} />
        ))}
      </Tabs>
      <TabPanel value={tabValue} index={0} padding={1}>
        <ResponsiveFormContainer columns={4} mb={2}>
          <MAutocomplete
            name="analyst"
            options={userList}
            label="Seleccione un analista"
            getOptionLabel={(option) => option.username || ""}
            validation={{ required: "El analista es requerido" }}
            disabled={isView}
          />
          <MDatePicker
            name="date"
            pickerType="date"
            label="Fecha calibración"
            validation={{ required: "La fecha es requerida" }}
            disabled={isView}
          />
          <MTextField
            name="identity_pattern_source"
            label="Nº de identificación de la fuente patrón"
            placeholder="Nº de identificación de la fuente patrón"
            validation={{
              required: "El número de identificación es requerido",
            }}
            fullWidth
            disabled={isView}
          />
          <MDatePicker
            name="date_calibration_certificate"
            pickerType="date"
            label="Fecha de calibración del certificado"
            disabled={isView}
          />
        </ResponsiveFormContainer>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows || []}
            columns={columns}
            disableRowSelectionOnClick
            hideFooterPagination
            getRowId={(row) => row.id}
            loading={loading}
          />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1} padding={1}>
        <ResponsiveFormContainer columns={4} mb={2}>
          <MDatePicker
            name="date_detector"
            pickerType="date"
            label="Fecha"
            validation={{ required: "La fecha es requerida" }}
            disabled={isView}
          />
          <MTextField
            name="identity_pattern_source"
            label="Nº de identificación de la fuente patrón"
            placeholder="Nº de identificación de la fuente patrón"
            fullWidth
            disabled
          />
          <MAutocomplete
            name="equipment"
            options={equipmentList}
            label="Seleccione un Equipo"
            getOptionLabel={(option) =>
              `${option.codigo} - ${option.descripcion}` || ""
            }
            validation={{ required: "El equipo es requerido" }}
            disabled={isView}
            loading={loadingEquipment}
          />
          <MAutocomplete
            name="detector"
            options={filterDetectors || []}
            label="Seleccione un Detector"
            getOptionLabel={(option) => `${option.name}` || ""}
            validation={{ required: "El detector es requerido" }}
            disabled={isView}
          />
          <MTextField
            name="mean_efficiency"
            label="Eficiencia media"
            placeholder="Eficiencia media"
            fullWidth
            disabled
          />
        </ResponsiveFormContainer>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows || []}
            columns={columnsDetector}
            disableRowSelectionOnClick
            hideFooterPagination
            getRowId={(row) => row.id}
            loading={loading}
          />
        </Box>
      </TabPanel>
    </>
  );
};

export default AnalysisSpectrometryAlfa;
